'use client';

import { FC, useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import clsx from 'clsx';
import { useTheme } from 'next-themes';
import { Category } from 'shared/types/product';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import { fontStyleMap, positionMap } from 'helpers/constants';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import { sdk } from 'sdk';
import { Reference } from 'types/reference';
import { useProjectSettings } from 'frontastic';
import { fontWeightMap } from '../product-category-slider/config';
import Tile from '../product-category-slider/tile';

export interface Props {
  key?: string | number;
  keyProp?: string | number;
  backgroundColor?: {
    darkColor: string;
    lightColor: string;
  };
  backgroundImage?: string | null;
  mounted?: boolean;
  tile: {
    backgroundImage?: string;
    title: string;
    image: ImageProps;
    target?: Reference;
    headLine?: Array<{
      headlineLabel: string;
      fontSizeDesktop: number;
      fontSizeMobile: number;
      textColor: string;
      textColorDark: string;
      textStyle: 'regular' | 'light' | 'medium' | 'semibold' | 'bold';
    }>;
  };
}

export interface SubCategoriesListProps {
  subCatConfig: {
    cardConfig: {
      fontSizeDesktop: number;
      fontSizeMobile: number;
      textColor: string;
      textColorDark: string;
      textStyle: 'regular' | 'light' | 'medium' | 'semibold' | 'bold';
    }[];
    containerConfig: {
      backImageAlt: string;
      backImageSrc: string;
      cardColor: string;
      cardColorDark: string;
      headingPostion: string;
      primaryHeading: {
        primaryHeadingText?: string;
        fontSizeDesktop: number;
        fontSizeMobile: number;
        primaryHeadingTextStyle: string;
        textColor: string;
        textColorDark: string;
      }[];
      secondaryHeading?: {
        secondaryHeadingText?: string;
        fontSizeDesktop: number;
        fontSizeMobile: number;
        secondaryHeadingTextStyle: string;
        textColor: string;
        textColorDark: string;
      }[];
    }[];
  }[];
  slides: {
    headlineLabel: string;
    imageAlt: string;
    imageSrc: string;
    link: Reference;
  }[];
}

const SubCategoriesList: FC<SubCategoriesListProps> = ({ subCatConfig, slides }) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { resolvedTheme = 'light' } = useTheme();
  const { slug } = useParams();
  const { projectSettings } = useProjectSettings();
  const { enabledIndividualPageForCategories } = useFeatures();

  const [category, setCategory] = useState<Category | null>(null);

  const { cardConfig, containerConfig } = subCatConfig?.[0] ?? {};

  const {
    backImageSrc,
    cardColor,
    cardColorDark,
    headingPostion = 'center',
    primaryHeading = [],
    secondaryHeading = [],
  } = containerConfig?.[0] ?? {};

  const { fontSizeDesktop, fontSizeMobile, textColor, textColorDark, textStyle } = cardConfig?.[0] ?? {};

  const primaryHeadingConfig = primaryHeading[0];
  const secondaryHeadingConfig = secondaryHeading[0];

  const categories = projectSettings?.categories ?? [];

  /**
   * When the flag is disabled, use the matching category from project settings.
   * When the flag is enabled, project settings category data is not used.
   */
  const categoryData = !enabledIndividualPageForCategories
    ? categories.find((item) => item.categoryKey === category?.categoryKey)
    : undefined;

  const navPosition: 'left' | 'right' = headingPostion === 'right' ? 'left' : 'right';

  const getHeadingStyles = (
    heading:
      | {
          fontSizeDesktop: number;
          fontSizeMobile: number;
          textColor: string;
          textColorDark: string;
        }
      | undefined,
  ) => {
    if (!heading) return {};

    return {
      fontSize: isDesktopSize ? `${heading.fontSizeDesktop}px` : `${heading.fontSizeMobile}px`,
      color: resolvedTheme === 'dark' ? heading.textColorDark : heading.textColor,
    };
  };

  /**
   * When enabledIndividualPageForCategories is true:
   *   Use CMS-configured slides.
   *
   * When enabledIndividualPageForCategories is false:
   *   Use category descendants from project settings.
   */
  const tiles = enabledIndividualPageForCategories
    ? slides?.map((entity) => ({
        image: {
          src: entity?.imageSrc,
          alt: entity?.imageAlt ?? entity?.headlineLabel ?? '',
        },
        title: entity?.headlineLabel ?? '',
        headLine: [
          {
            headlineLabel: entity?.headlineLabel ?? '',
            fontSizeDesktop,
            fontSizeMobile,
            textColor,
            textColorDark,
            textStyle,
          },
        ],
        target: entity.link,
      }))
    : categoryData?.descendants?.map((entity) => ({
        image: {
          src: entity?.categoryImage,
          alt: entity?.categoryImageAlt ?? entity?.name ?? '',
        },
        title: entity?.name ?? '',
        headLine: [
          {
            headlineLabel: entity?.name ?? '',
            fontSizeDesktop,
            fontSizeMobile,
            textColor,
            textColorDark,
            textStyle,
          },
        ],
        target: entity.categoryURL,
      }));

  /**
   * Fetch category data only when the feature flag is disabled.
   */
  useEffect(() => {
    if (enabledIndividualPageForCategories) {
      setCategory(null);
      return;
    }

    const fetchCategoryOfSlug = async () => {
      const categorySlug = Array.isArray(slug) ? slug[0] : slug;

      const response = await sdk.composableCommerce.product.queryCategories({
        slug: categorySlug ?? 'print',
        limit: 1,
      });

      if (!response?.isError && response?.data?.count > 0) {
        setCategory(response.data.items?.[0] ?? null);
      } else {
        setCategory(null);
      }
    };

    void fetchCategoryOfSlug();
  }, [enabledIndividualPageForCategories, slug]);

  useEffect(() => {
    if (typeof globalThis === 'undefined' || !globalThis.history) return;

    const hasScrollRestoration: boolean = 'scrollRestoration' in globalThis.history;

    let prevValue: History['scrollRestoration'] | undefined;

    if (hasScrollRestoration) {
      prevValue = globalThis.history.scrollRestoration;
      globalThis.history.scrollRestoration = 'manual';
    }

    return () => {
      if (hasScrollRestoration) {
        globalThis.history.scrollRestoration = prevValue ?? 'auto';
      }
    };
  }, []);

  useEffect(() => {
    if (typeof globalThis === 'undefined' || typeof globalThis.scrollTo !== 'function') return;

    globalThis.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' as ScrollBehavior,
    });
  }, []);

  /**
   * When the flag is enabled, headings come from the CMS configuration.
   * When the flag is disabled, headings come from project settings.
   */
  const primaryHeadingText = enabledIndividualPageForCategories
    ? primaryHeadingConfig?.primaryHeadingText
    : categoryData?.heading;

  const secondaryHeadingText = enabledIndividualPageForCategories
    ? secondaryHeadingConfig?.secondaryHeadingText
    : categoryData?.subHeading;

  return (
    <div data-ffid="ctr-clp" className="mx-0 pt-24 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <div className="container px-12 sm:px-0">
        <div
          className={clsx('heading-category-slider flex gap-4 px-4', navPosition === 'right' && 'justify-between')}
          data-ffid="ctr-heading-container"
        >
          <div className={clsx('flex flex-1 text-wrap', positionMap[headingPostion] || positionMap.center)}>
            <div className={clsx('max-w-[920px]', headingPostion === 'center' && 'ml-8')}>
              {!!primaryHeadingText && (
                <h2
                  className={clsx(
                    'heading-title',
                    primaryHeadingConfig && fontWeightMap[primaryHeadingConfig.primaryHeadingTextStyle],
                  )}
                  style={getHeadingStyles(primaryHeadingConfig)}
                  data-ffid="lbl-primary-heading"
                >
                  {primaryHeadingText}
                </h2>
              )}

              {!!secondaryHeadingText && (
                <h3
                  className={clsx(
                    'heading-title',
                    positionMap[headingPostion] || positionMap.center,
                    headingPostion === 'center' && 'pl-8',
                    secondaryHeadingConfig && fontStyleMap[secondaryHeadingConfig.secondaryHeadingTextStyle],
                  )}
                  style={getHeadingStyles(secondaryHeadingConfig)}
                  data-ffid="lbl-secondary-heading"
                >
                  {secondaryHeadingText}
                </h3>
              )}
            </div>
          </div>
        </div>

        {!!tiles?.length && (
          <div className="grid w-full grid-cols-2 gap-12 py-24 md:grid-cols-4 md:gap-24 md:gap-y-40 md:py-36">
            {tiles.map((tile, index) => (
              <Tile
                key={`${tile.title}-${index}`}
                keyProp={index}
                tile={tile}
                backgroundColor={{
                  lightColor: cardColor,
                  darkColor: cardColorDark,
                }}
                backgroundImage={backImageSrc}
                isCarousel={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubCategoriesList;
